import type { FormEditorEntity } from "../form-editor-entity.types";

export type FormEditorScope = "admin" | "org";
export type FormEditorVariant = "standalone" | "full";

const ENTITY_CONFIG = {
	ticket: {
		segment: "ticketTypes",
		listTitleKey: "admin.ticketTypes.table.title",
	},
	job: {
		segment: "jobTypes",
		listTitleKey: "admin.jobTypes.table.title",
	},
	asset: {
		segment: "assetTypes",
		listTitleKey: "admin.assetTypes.table.title",
	},
	record: {
		segment: "recordTypes",
		listTitleKey: "admin.recordTypes.table.title",
	},
} as const satisfies Record<
	FormEditorEntity,
	{ segment: string; listTitleKey: string }
>;

export interface BuildFormEditorPathsInput {
	scope: FormEditorScope;
	orgId?: string;
	entity: FormEditorEntity;
	typeId: string;
	variant: FormEditorVariant;
}

export interface FormEditorPaths {
	listHref: string;
	/** Middle breadcrumb — type detail (full page). */
	detailHref: string;
	/** Drawer details route (standalone middle crumb). */
	drawerDetailsHref: string;
	formBaseHref: string;
}

/**
 * Builds list, detail, and editor base paths for admin and org-admin form editors.
 */
export function buildFormEditorPaths({
	scope,
	orgId,
	entity,
	typeId,
	variant,
}: BuildFormEditorPathsInput): FormEditorPaths {
	const { segment } = ENTITY_CONFIG[entity];
	const typeBase =
		scope === "org" && orgId
			? `/org-admin/${orgId}/configuration/${segment}/${typeId}`
			: `/admin/configuration/${segment}/${typeId}`;

	const listHref =
		scope === "org" && orgId
			? `/org-admin/${orgId}/configuration/${segment}`
			: `/admin/configuration/${segment}`;

	return {
		listHref,
		detailHref: `${typeBase}/full`,
		drawerDetailsHref: `${typeBase}/details`,
		formBaseHref:
			variant === "full" ? `${typeBase}/full/form` : `${typeBase}/form`,
	};
}

/**
 * i18n key for the entity list label in the form editor breadcrumb.
 */
export function getFormEditorListTitleKey(entity: FormEditorEntity): string {
	return ENTITY_CONFIG[entity].listTitleKey;
}

export const FULL_FORM_PREVIEW_SEARCH = {
	tab: "form",
	mode: "view",
	presentation: "full",
} as const;

type NavigateLike = (options: {
	to: string;
	params?: Record<string, string>;
	search?: object;
}) => void;

/**
 * Navigates from `/full/form` back to the full-page form preview tab.
 * For record types, `subOrganizationId` is preserved so the preview loads
 * the right sub-org-scoped form definition.
 */
export function navigateToFullFormPreview(
	navigate: NavigateLike,
	input: Omit<BuildFormEditorPathsInput, "variant"> & {
		subOrganizationId?: string | null;
	},
): void {
	const { entity, typeId, scope, orgId, subOrganizationId } = input;

	if (scope === "org" && orgId) {
		switch (entity) {
			case "ticket":
				navigate({
					to: "/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/full",
					params: { orgId, ticketTypeId: typeId },
					search: FULL_FORM_PREVIEW_SEARCH,
				});
				return;
			case "job":
				navigate({
					to: "/org-admin/$orgId/configuration/jobTypes/$jobTypeId/full",
					params: { orgId, jobTypeId: typeId },
					search: FULL_FORM_PREVIEW_SEARCH,
				});
				return;
			case "asset":
				navigate({
					to: "/org-admin/$orgId/configuration/assetTypes/$assetTypeId/full",
					params: { orgId, assetTypeId: typeId },
					search: FULL_FORM_PREVIEW_SEARCH,
				});
				return;
			case "record":
				navigate({
					to: "/org-admin/$orgId/configuration/recordTypes/$recordTypeId/full",
					params: { orgId, recordTypeId: typeId },
					search: {
						...FULL_FORM_PREVIEW_SEARCH,
						...(subOrganizationId ? { subOrganizationId } : {}),
					},
				});
				return;
		}
	}

	switch (entity) {
		case "ticket":
			navigate({
				to: "/admin/configuration/ticketTypes/$ticketTypeId/full",
				params: { ticketTypeId: typeId },
				search: FULL_FORM_PREVIEW_SEARCH,
			});
			return;
		case "job":
			navigate({
				to: "/admin/configuration/jobTypes/$jobTypeId/full",
				params: { jobTypeId: typeId },
				search: FULL_FORM_PREVIEW_SEARCH,
			});
			return;
		case "asset":
			navigate({
				to: "/admin/configuration/assetTypes/$assetTypeId/full",
				params: { assetTypeId: typeId },
				search: FULL_FORM_PREVIEW_SEARCH,
			});
			return;
		case "record":
			navigate({
				to: "/admin/configuration/recordTypes/$recordTypeId/full",
				params: { recordTypeId: typeId },
				search: {
					...FULL_FORM_PREVIEW_SEARCH,
					...(subOrganizationId ? { subOrganizationId } : {}),
				},
			});
	}
}
